import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Menu, X, LayoutDashboard } from "lucide-react";
import axios from "axios";
import { useLang } from "../../lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const { lang, setLang, t } = useLang();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("shamar_token");
    if (!token) return;
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => setIsAdmin(true))
      .catch(() => setIsAdmin(false));
  }, []);

  const links = [
    { label: t.nav.piliers, href: "#piliers" },
    { label: t.nav.formules, href: "#formules" },
    { label: t.nav.experiences, href: "#experiences" },
    { label: t.nav.table, href: "#galerie" },
    { label: t.nav.reserver, href: "#reserver" },
  ];

  const LangToggle = ({ mobile }) => (
    <button
      onClick={() => setLang(lang === "fr" ? "en" : "fr")}
      data-testid={mobile ? "lang-toggle-mobile" : "lang-toggle"}
      className={`text-[13px] tracking-[0.14em] uppercase border px-3 py-1.5 transition-colors duration-300 ${
        mobile
          ? "border-white/30 text-[#F0F0F0] hover:border-[#C9A03C] hover:text-[#C9A03C] w-fit"
          : "border-[#0A192F]/20 text-[#0A192F]/70 hover:border-[#C9A03C] hover:text-[#C9A03C]"
      }`}
      aria-label="Switch language"
    >
      {lang === "fr" ? "EN" : "FR"}
    </button>
  );

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-shadow duration-500 bg-[#FAFAFA]/90 backdrop-blur-xl border-b border-black/5 ${
        scrolled ? "shadow-sm" : ""
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        <a href="#accueil" className="flex items-center group" data-testid="header-logo">
          <img
            src="/logo-shamar.png"
            alt="Shamar — Services sur mesure"
            className="h-14 w-auto transition-transform duration-500 group-hover:scale-[1.03]"
          />
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.href.slice(1)}`}
              className="text-[13px] tracking-[0.14em] uppercase transition-colors duration-300 hover:text-[#C9A03C] text-[#0A192F]/80"
            >
              {l.label}
            </a>
          ))}
          <a
            href="#devis"
            data-testid="nav-devis"
            className="text-[13px] tracking-[0.14em] uppercase px-6 py-2.5 border border-[#C9A03C] text-[#C9A03C] hover:bg-[#C9A03C] hover:text-[#050B14] transition-colors duration-400"
          >
            {t.nav.devis}
          </a>
          <LangToggle />
          {isAdmin && (
            <a
              href="/admin"
              data-testid="nav-admin"
              className="flex items-center gap-1.5 text-[13px] tracking-[0.14em] uppercase text-[#0A192F] hover:text-[#C9A03C] transition-colors duration-300"
              title={t.nav.adminMobile}
            >
              <LayoutDashboard className="w-4 h-4" /> {t.nav.admin}
            </a>
          )}
        </nav>

        <button
          className="md:hidden text-[#0A192F]"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-[#0A192F] border-t border-white/10 px-6 py-8 flex flex-col gap-6" data-testid="mobile-menu">
          {links.map((l) => (
            <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="text-[#F0F0F0] tracking-[0.14em] uppercase text-sm">
              {l.label}
            </a>
          ))}
          <a href="#devis" onClick={() => setOpen(false)} className="text-[#C9A03C] tracking-[0.14em] uppercase text-sm">
            {t.nav.devis}
          </a>
          <LangToggle mobile />
          {isAdmin && (
            <a href="/admin" data-testid="nav-admin-mobile" className="flex items-center gap-1.5 text-[#F0F0F0] tracking-[0.14em] uppercase text-sm">
              <LayoutDashboard className="w-4 h-4" /> {t.nav.adminMobile}
            </a>
          )}
        </div>
      )}
    </motion.header>
  );
};
